import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PathwayInput as Input } from "../../../_UI/StyledComponents/PathwayForm";
import { clientReducerActions } from "../../../_services/store";
import stringUtils from "../../../_services/string-utils";
import PathwayChoiceGrid from "../../../_UI/Components/PathwayChoiceGrid";
import { PathwayButton } from "../../../_UI/Components/PathwayButtons";
import { getIntegrationIcon, getIntegrationLogo } from "../../../_statics/IntegrationBrand";
import { AuthDataStatus } from "../../../_statics/AuthDataStatus";
import { AuthDataIdentifier } from "../../../_statics/AuthDataType";
import { Modes } from "../../../_statics/CRUDStatics";
import { InfoMessage, SuccessMessage, WarningMessage, ErrorMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import PathwaySingleFileInputGroup from "../../../_UI/Components/Inputs/PathwaySingleFileInputGroup";
import { PathwayFormGroup } from "../../../_UI/StyledComponents/PathwayForm";
import { ClientScreenInputGrid, EXTERNAL_AUTH_CONNECTIONS } from "../screens/ClientsScreen";
import {
    buildSelectableConnections,
    mapConnectAuthDataTypeToIntegrationName,
    mapConnectedAuthDataInputFieldInformation,
} from "../services/client.mapper";
import {
    getRedirectingLinkBySelectedAuthDataType,
    updateConnectedAuthDataForClient,
} from "../services/clients.actions";
import { initialClient } from "../services/clients.reducer";
import styled from "styled-components";
import { LoadingSpinnerButton } from "../../../_UI/Components/PathwayButtons";
import { Integration } from "../../../_statics/Integration";
import { testZalandoConnection } from "../../../integrations/shop/Zalando/store/zalando.actions";
import { PathwayLabel } from "../../../_UI/StyledComponents/PathwayForm";
import { PathwayTooltip } from "../../../_UI/Components/PathwayTooltip";
import { PathwayPopover } from "../../../_UI/Components/PathwayPopover";

export const generateBase64SecretKey = () => {
    // Generate 48 random bytes (384 bits) for high security
    const randomBytes = new Uint8Array(48);
    crypto.getRandomValues(randomBytes);

    return btoa(String.fromCharCode(...randomBytes));
};

const ConnectedAuthDataInput = (props) => {
    const authDataTypes = useSelector((state) => state.commons.authDataTypes);
    const clientReducer = useSelector((state) => state.client);
    const currentUser = useSelector((state) => state.authentication.user);
    const allowedApps = useSelector((state) => state.authentication.user?.selectedOrganization?.allowedApps);
    const zalando = useSelector((state) => state.integrations[Integration.ZALANDO.lowercaseIdentifier]);
    const dispatch = useDispatch();

    const handleZalandoTestConnection = () => {
        const data = clientReducer.editConnectedAuthData;
        dispatch(
            testZalandoConnection(data.clientUUID, {
                clientId: (clientReducer.editAccessData.key || data.key || "").trim(),
                clientSecret: (clientReducer.editAccessData.value || data.value || "").trim(),
                merchantId: (clientReducer.editAccessData.endpoint || data.endpoint || "").trim(),
                environment: "PRODUCTION",
            })
        );
    };

    const [relatedClient, setRelatedClient] = useState({});

    useEffect(() => {
        const client = clientReducer.clients.find(
            (client) => client.externalUUID === clientReducer.editConnectedAuthData.clientUUID
        );
        if (client) {
            setRelatedClient(client);
        } else if (!client && props.selectedClientForAuthData) {
            setRelatedClient(initialClient);
        } else {
            setRelatedClient({});
        }
    }, [clientReducer.editConnectedAuthData, clientReducer.clients, props.selectedClientForAuthData]);

    const selectableConnections = useMemo(
        () => buildSelectableConnections(authDataTypes, relatedClient, allowedApps, currentUser.userRole),
        [authDataTypes, relatedClient, allowedApps, currentUser.userRole]
    );

    const chooseConnectedAuthDataType = (e) => {
        const { name, value } = e.target;
        if (!stringUtils.isBlank(clientReducer.editConnectedAuthData.prepareAuthDataOnboardingErrorMessage)) {
            dispatch(
                clientReducerActions.setEditConnectedAuthData({
                    ...clientReducer.editConnectedAuthData,
                    [name]: value,
                    prepareAuthDataOnboardingErrorMessage: null,
                })
            );
        } else {
            dispatch(
                clientReducerActions.setEditConnectedAuthData({
                    ...clientReducer.editConnectedAuthData,
                    [name]: value,
                })
            );
        }

        dispatch(
            clientReducerActions.setInputLableInformation(
                mapConnectedAuthDataInputFieldInformation({
                    ...clientReducer.editConnectedAuthData,
                    [name]: value,
                })
            )
        );
        if (name === "type") {
            if (EXTERNAL_AUTH_CONNECTIONS.includes(value)) {
                const authLink = dispatch(
                    getRedirectingLinkBySelectedAuthDataType(value, clientReducer.editConnectedAuthData.clientUUID)
                );
                if (authLink) {
                    dispatch(clientReducerActions.setAuthLink(authLink));
                }
            }
            if (value === AuthDataIdentifier.ADYEN) {
                dispatch(
                    clientReducerActions.initializeAdyenAccessData(clientReducer.editConnectedAuthData.clientUUID)
                );
            }
        }
        props.setConnectedAuthErrorForClient && props.setConnectedAuthErrorForClient(null);
    };

    const chosenConnection = selectableConnections.find(
        (connection) => connection.enumIdentifier === clientReducer.editConnectedAuthData.type
    );

    return (
        <ClientScreenInputGrid>
            {stringUtils.equals(clientReducer.editConnectedAuthDataMode, Modes.CREATE) ? (
                <PathwayFormGroup>
                    <PathwayLabel>Integration wählen *</PathwayLabel>
                    {/* The grid collapses once a choice is made. Its 25 tiles are only needed until then, and
                        leaving them open pushed the fields that follow far below the fold — the dialog was
                        mostly a list of things already decided. */}
                    {chosenConnection ? (
                        <ChosenRow data-cy="connection-type-chosen">
                            {getIntegrationLogo(chosenConnection.enumIdentifier) ? (
                                <ChosenMark src={getIntegrationLogo(chosenConnection.enumIdentifier)} alt="" />
                            ) : (
                                <i className={getIntegrationIcon(chosenConnection.enumIdentifier)} aria-hidden="true" />
                            )}
                            <ChosenName>{chosenConnection.enumDescription}</ChosenName>
                            {clientReducer.editConnectedAuthDataMode !== Modes.EDIT && !props.disabled && (
                                <PathwayButton
                                    color="secondary"
                                    onClick={() => chooseConnectedAuthDataType({ target: { name: "type", value: "" } })}
                                    dataCy="connection-type-change"
                                >
                                    Ändern
                                </PathwayButton>
                            )}
                        </ChosenRow>
                    ) : (
                        <PathwayChoiceGrid
                            name="type"
                            label="Integration wählen"
                            dataCy="connection-type"
                            value={clientReducer.editConnectedAuthData.type}
                            onChange={(value) => chooseConnectedAuthDataType({ target: { name: "type", value } })}
                            options={selectableConnections.map((connection) => ({
                                value: connection.enumIdentifier,
                                label: connection.enumDescription,
                                logo: getIntegrationLogo(connection.enumIdentifier),
                                icon: getIntegrationIcon(connection.enumIdentifier),
                                disabled: clientReducer.editConnectedAuthDataMode === Modes.EDIT || props.disabled,
                            }))}
                        />
                    )}

                    {(clientReducer.editConnectedAuthData.type === AuthDataIdentifier.SHOPIFY_APPSTORE_BILLING ||
                        clientReducer.editConnectedAuthData.type === AuthDataIdentifier.SHOPIFY_APPSTORE_PLATFORM) && (
                        <small>
                            <br />
                            <strong>
                                Bitte stelle sicher, dass du nach der Weiterleitung zu Shopify den richtigen Shop
                                auswählst, falls du in mehreren Shops Admin bist. Die Verbindung wird immer zu dem Shop
                                hergestellt in dem du aktuell angemeldet bist.
                            </strong>
                        </small>
                    )}

                    {clientReducer.editConnectedAuthData.type === AuthDataIdentifier.AMAZON && (
                        <small>
                            <br />
                            <strong>
                                Dein Amazon Account wird mit dem Drücken auf "Verbinden" verbunden und wir werden
                                anschliessend dein Setup aktualisieren. Bitte lies dir folgenden Wissensartikel durch um
                                zu erfahren was sich durch die Umstellung auf die Amazon API für dich ändert:
                                <a
                                    href="https://hilfe.pathway-solutions.de/de/knowledge/amazon/wechsel-api-integration"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {" "}
                                    Verbindungen zu Amazon
                                </a>
                            </strong>
                        </small>
                    )}

                    {clientReducer.editConnectedAuthData.type === AuthDataIdentifier.PAYPAL && (
                        <small>
                            <strong>
                                Die folgenden Voraussetzungen müssen erfüllt sein, damit die PayPal API erstellt werden
                                kann:
                            </strong>
                            <br />
                            <ul>
                                <li>
                                    Um die API hinzuzufügen, musst du <strong>Admin / Accountowner</strong> sein.
                                </li>
                                <li>
                                    Du musst die <strong>primären Login-Daten</strong> verwenden, das heißt, du kannst
                                    dich nicht mit einem Benutzernamen anmelden, sondern musst die E-Mail-Adresse des
                                    Haupt-Accounts verwenden.
                                </li>
                                <li>
                                    Bitte prüfe deine <strong>E-Mail-Adresse</strong> bei der PayPal Anmeldung. Wir
                                    geben automatisch die E-Mail, mit der du dich auf der Plattform angemeldet hast, an.
                                    Bitte stell sicher, dass du das PayPal Konto angibst, welches bei euch im Shop
                                    verwendet wird.
                                </li>
                                <li>
                                    Dein Konto muss ein <strong>Geschäftskonto</strong> sein.
                                </li>
                            </ul>
                        </small>
                    )}

                    {clientReducer.editConnectedAuthData.type === AuthDataIdentifier.OTTO_OAUTH && (
                        <small>
                            <br />
                            <strong>
                                Bei OTTO durchläufst du zwei Bestätigungen: zuerst die Installation der App,
                                anschließend die Autorisierung. Beide Schritte laufen automatisch nacheinander ab.
                            </strong>
                        </small>
                    )}
                </PathwayFormGroup>
            ) : (
                <>
                    <PathwayFormGroup>
                        <PathwayLabel>
                            {mapConnectAuthDataTypeToIntegrationName(clientReducer.editConnectedAuthData.type)}{" "}
                            Verbindung
                        </PathwayLabel>
                        <Input
                            type="select"
                            name="type"
                            onChange={chooseConnectedAuthDataType}
                            disabled={true}
                            value={clientReducer.editConnectedAuthData.type}
                        >
                            <option value={clientReducer.editConnectedAuthData.type}>
                                {clientReducer.editConnectedAuthData.title}
                            </option>
                        </Input>
                    </PathwayFormGroup>

                    {clientReducer.editConnectedAuthData.status === AuthDataStatus.SETUP &&
                        clientReducer.editConnectedAuthData.type === AuthDataIdentifier.PAYPAL &&
                        stringUtils.isBlank(clientReducer.connectedAuthData.endpoint) && (
                            <>
                                <WarningMessage marginless={true}>
                                    <p>
                                        Wir warten aktuell auf eine Bestätigung der Verbindung. Bitte lade diese Seite
                                        in ein paar Minuten erneut.
                                    </p>
                                    <br />
                                    <p>
                                        Sollte die Einrichtung nach 15 Minuten nicht erfolgreich sein, klicke bitte auf
                                        “Neu verbinden” und versuche es nochmal.
                                    </p>
                                    <br />
                                    Alternativ kannst du hier manuell deine Merchant ID eintragen. Eine Anleitung dazu
                                    findest du in folgendem Hilfeartikel:{" "}
                                    <a
                                        href={"https://hilfe.pathway-solutions.de/de/knowledge/paypal/api-fehlerhaft"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        PayPal API fehlgeschlagen
                                    </a>
                                </WarningMessage>
                            </>
                        )}

                    {clientReducer.editConnectedAuthData.status === AuthDataStatus.SETUP &&
                        clientReducer.editConnectedAuthData.type === AuthDataIdentifier.MOLLIE && (
                            <WarningMessage marginless={true}>
                                Die Mollie Verbindung wird noch geprüft und sollte in den nächsten 30 Minuten bestätigt
                                werden.
                            </WarningMessage>
                        )}

                    {EXTERNAL_AUTH_CONNECTIONS.includes(clientReducer.editConnectedAuthData.type) &&
                        clientReducer.connectedAuthData.endpoint && (
                            <PathwayFormGroup>
                                <PathwayLabel>Endpunkt - Verbindung</PathwayLabel>
                                <Input
                                    name="type"
                                    onChange={chooseConnectedAuthDataType}
                                    disabled={true}
                                    value={clientReducer.editConnectedAuthData.endpoint}
                                />
                            </PathwayFormGroup>
                        )}
                </>
            )}

            {clientReducer.editConnectedAuthData.type && clientReducer.editConnectedAuthData.type !== "" && (
                <>
                    {clientReducer.editConnectedAuthData.type === AuthDataIdentifier.PAYPAL &&
                        clientReducer.editConnectedAuthData.status === AuthDataStatus.SETUP && (
                            <PaypalFieldLayout>
                                <PathwayFormGroup>
                                    <PathwayLabel>{clientReducer.inputLableInformation.endpoint.displayName}</PathwayLabel>
                                    <Input
                                        type="text"
                                        name="endpoint"
                                        data-cy="endpoint"
                                        placeholder={clientReducer.inputLableInformation.endpoint.placeholder}
                                        onChange={props.handleConnectedAuthDataChange}
                                        value={clientReducer.editAccessData.endpoint}
                                        disabled={clientReducer.inputLableInformation.endpoint.disabled}
                                        onBlur={props.clearWhitespaces}
                                    />
                                </PathwayFormGroup>
                                <LoadingSpinnerButton
                                    loading={clientReducer.isEditAuthDataLoading}
                                    onClick={() =>
                                        dispatch(updateConnectedAuthDataForClient(clientReducer.editConnectedAuthData))
                                    }
                                    buttonCyData={"save-paypal-auth-data-button"}
                                >
                                    Speichern
                                </LoadingSpinnerButton>
                            </PaypalFieldLayout>
                        )}

                    {!EXTERNAL_AUTH_CONNECTIONS.includes(clientReducer.editConnectedAuthData.type) &&
                        !(
                            clientReducer.editConnectedAuthData.type === AuthDataIdentifier.PAYPAL &&
                            clientReducer.editConnectedAuthData.status === AuthDataStatus.SETUP
                        ) && (
                            <>
                                {clientReducer.inputLableInformation.endpoint.displayName && (
                                    <PathwayFormGroup>
                                        <LabelWithTooltip>
                                            <PathwayLabel>{clientReducer.inputLableInformation.endpoint.displayName}</PathwayLabel>
                                            {clientReducer.inputLableInformation.endpoint.tooltip && (
                                                <PathwayTooltip id="endpoint-tooltip">
                                                    {clientReducer.inputLableInformation.endpoint.tooltip}
                                                </PathwayTooltip>
                                            )}
                                        </LabelWithTooltip>
                                        <Input
                                            type="text"
                                            name="endpoint"
                                            data-cy="endpoint"
                                            placeholder={clientReducer.inputLableInformation.endpoint.placeholder}
                                            onChange={props.handleConnectedAuthDataChange}
                                            value={clientReducer.editAccessData.endpoint}
                                            disabled={
                                                clientReducer.inputLableInformation.endpoint.disabled ||
                                                clientReducer.editConnectedAuthData.type === AuthDataIdentifier.ADYEN
                                            }
                                            onBlur={props.clearWhitespaces}
                                        />
                                    </PathwayFormGroup>
                                )}

                                {clientReducer.inputLableInformation.key.displayName && (
                                    <PathwayFormGroup>
                                        <PathwayLabel>{clientReducer.inputLableInformation.key.displayName}</PathwayLabel>
                                        <Input
                                            type="text"
                                            name="key"
                                            data-cy="key"
                                            placeholder={clientReducer.inputLableInformation.key.placeholder}
                                            value={clientReducer.editAccessData.key}
                                            onChange={props.handleConnectedAuthDataChange}
                                            disabled={
                                                clientReducer.editConnectedAuthData.type === AuthDataIdentifier.ADYEN
                                            }
                                            onBlur={props.clearWhitespaces}
                                        />
                                    </PathwayFormGroup>
                                )}

                                {clientReducer.inputLableInformation.value.displayName &&
                                    clientReducer.editConnectedAuthData.type === AuthDataIdentifier.AMAZON_PAY_V2 && (
                                        <>
                                            <PathwaySingleFileInputGroup
                                                label={"Amazon .pem Datei"}
                                                type="file"
                                                name={"privateKeyFileUpload"}
                                                handleChange={props.handleFileUpload}
                                                value={clientReducer?.dataFileToImport}
                                                dataCy={"amazon-pay-private-key-file-input"}
                                            />
                                            <InfoMessage>
                                                Hinweis: in diesem
                                                <a
                                                    href="https://developer.amazon.com/de/docs/amazon-pay-checkout/get-set-up-for-integration.html#5-get-your-public-key-id"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    &nbsp;Wissensartikel
                                                </a>{" "}
                                                findest du eine Anleitung wie du den Public Key bei Amazon erhältst.
                                                Bitte beachte, dass du den Public Key für{" "}
                                                <b>Amazon Pay (Production View)</b> benötigst.
                                            </InfoMessage>
                                        </>
                                    )}
                                {clientReducer.inputLableInformation.value.displayName &&
                                    clientReducer.editConnectedAuthData.type !== AuthDataIdentifier.AMAZON_PAY_V2 &&
                                    clientReducer.editConnectedAuthData.type !== AuthDataIdentifier.ZALANDO && (
                                        <PathwayFormGroup>
                                            <PathwayLabel>{clientReducer.inputLableInformation.value.displayName}</PathwayLabel>
                                            <Input
                                                type="text"
                                                name="value"
                                                data-cy="value"
                                                placeholder={clientReducer.inputLableInformation.value.placeholder}
                                                value={
                                                    clientReducer.editAccessData.value ||
                                                    clientReducer.editConnectedAuthData.value
                                                }
                                                onChange={props.handleConnectedAuthDataChange}
                                                onBlur={props.clearWhitespaces}
                                            />
                                        </PathwayFormGroup>
                                    )}
                                {clientReducer.inputLableInformation.value.displayName &&
                                    clientReducer.editConnectedAuthData.type === AuthDataIdentifier.ZALANDO && (
                                        <>
                                            <PathwayFormGroup>
                                                <PathwayLabel>{clientReducer.inputLableInformation.value.displayName}</PathwayLabel>
                                                <Input
                                                    type="password"
                                                    name="value"
                                                    data-cy="value"
                                                    autoComplete="new-password"
                                                    placeholder={clientReducer.inputLableInformation.value.placeholder}
                                                    value={
                                                        clientReducer.editAccessData.value ||
                                                        clientReducer.editConnectedAuthData.value
                                                    }
                                                    onChange={props.handleConnectedAuthDataChange}
                                                    onBlur={props.clearWhitespaces}
                                                />
                                            </PathwayFormGroup>
                                            <PathwayFormGroup>
                                                <LoadingSpinnerButton
                                                    color="secondary"
                                                    buttonCyData="zalando-test-connection-button"
                                                    loading={zalando?.isTestConnectionLoading}
                                                    onClick={handleZalandoTestConnection}
                                                >
                                                    Verbindung testen
                                                </LoadingSpinnerButton>
                                            </PathwayFormGroup>
                                            {zalando?.testConnectionResult?.success === true && (
                                                <SuccessMessage data-cy="zalando-test-connection-success">
                                                    Verbindung erfolgreich — Händler:{" "}
                                                    {zalando.testConnectionResult.merchantName ||
                                                        zalando.testConnectionResult.merchantId}
                                                </SuccessMessage>
                                            )}
                                            {zalando?.testConnectionResult?.success === false && (
                                                <WarningMessage data-cy="zalando-test-connection-failure">
                                                    {zalando.testConnectionResult.errorMessage ||
                                                        "Verbindung fehlgeschlagen"}
                                                </WarningMessage>
                                            )}
                                            {zalando?.testConnectionErrorMessage && (
                                                <ErrorMessage data-cy="zalando-test-connection-error">
                                                    {typeof zalando.testConnectionErrorMessage === "string"
                                                        ? zalando.testConnectionErrorMessage
                                                        : zalando.testConnectionErrorMessage?.message ||
                                                          "Verbindung fehlgeschlagen"}
                                                </ErrorMessage>
                                            )}
                                        </>
                                    )}
                                {clientReducer.inputLableInformation.sharedSecret &&
                                    clientReducer.inputLableInformation.sharedSecret.displayName && (
                                        <PathwayFormGroup>
                                            <PathwayLabel>
                                                {clientReducer.inputLableInformation.sharedSecret.displayName}
                                            </PathwayLabel>
                                            <Input
                                                type="text"
                                                name="sharedSecret"
                                                data-cy="sharedSecret"
                                                placeholder={
                                                    clientReducer.inputLableInformation.sharedSecret.placeholder
                                                }
                                                value={clientReducer.editAccessData.sharedSecret}
                                                onChange={props.handleConnectedAuthDataChange}
                                                onBlur={props.clearWhitespaces}
                                            />
                                        </PathwayFormGroup>
                                    )}

                                {clientReducer.inputLableInformation.mfaKey &&
                                    clientReducer.inputLableInformation.mfaKey.displayName && (
                                        <PathwayFormGroup>
                                            <LabelWithTooltip>
                                                <PathwayLabel>{clientReducer.inputLableInformation.mfaKey.displayName}</PathwayLabel>
                                                {clientReducer.inputLableInformation.mfaKey.kbLink && (
                                                    <PathwayPopover id="mfaKey-popover">
                                                        Das ist der Schlüssel von Unzer und{" "}
                                                        <strong>nicht</strong> der Code in der
                                                        Authenticator-App – mehr Infos im
                                                        Wissensartikel, Schritt 1 bei Punkt 7.{" "}
                                                        <a
                                                            href={clientReducer.inputLableInformation.mfaKey.kbLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Zum Artikel
                                                        </a>
                                                    </PathwayPopover>
                                                )}
                                            </LabelWithTooltip>
                                            <Input
                                                type="text"
                                                name="mfaKey"
                                                data-cy="mfaKey"
                                                placeholder={clientReducer.inputLableInformation.mfaKey.placeholder}
                                                value={clientReducer.editAccessData.mfaKey}
                                                onChange={props.handleConnectedAuthDataChange}
                                                onBlur={props.clearWhitespaces}
                                            />
                                            {clientReducer.inputLableInformation.mfaKey.kbLink && (
                                                <small>
                                                    <a
                                                        href={clientReducer.inputLableInformation.mfaKey.kbLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-cy="mfaKey-kb-link"
                                                    >
                                                        Unzer API verbinden
                                                    </a>
                                                </small>
                                            )}
                                        </PathwayFormGroup>
                                    )}
                            </>
                        )}
                </>
            )}

            {clientReducer.editConnectedAuthData.type && clientReducer.editConnectedAuthData.type !== "" && <></>}
        </ClientScreenInputGrid>
    );
};

const LabelWithTooltip = styled.div`
    display: flex;
    align-items: center;
`;

const PaypalFieldLayout = styled.div`
    display: grid;
    grid-template-columns: auto 130px;
    align-items: end;
    column-gap: var(--gap-l);

    * {
        row-gap: 0;
    }
`;

export default ConnectedAuthDataInput;

const ChosenRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--gap-l);
    width: 100%;
    min-height: var(--pw-control-height);
    padding: 8px 12px;
    border: 1px solid var(--pw-border-brand);
    border-radius: var(--pw-radius-lg);
    background: var(--pw-tint-brand);
`;

const ChosenMark = styled.img`
    width: 20px;
    height: 20px;
    object-fit: contain;
    flex: none;
`;

const ChosenName = styled.span`
    flex: 1;
    min-width: 0;
    font-size: var(--pw-text-control);
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
`;
